import { ChangeEvent, useCallback } from 'react';
import clsx from 'clsx';

import { Select } from '../../../components/inputs';
import { SizeEnum } from '../../../types';

import { Material } from '../../materials/types';

import { ProductTemplateSelections } from '../types';

const LEFT_ICON_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'iron', label: 'Iron' },
  { value: 'press-and-stick', label: 'Press-and-Stick' },
  { value: 'tag', label: 'Tagged' },
  { value: 'tagless', label: 'Tagless' },
  { value: 'water', label: 'Water' }
];

const COLOR_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'blush', label: 'Blush' },
  { value: 'coral', label: 'Coral' },
  { value: 'lemon', label: 'Lemon' },
  { value: 'turquoise', label: 'Turquoise' }
];

const LEFT_TEXT_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'iron-on', label: 'Iron On' },
  { value: 'laundry-safe', label: 'Laundry Safe' },
  { value: 'press-and-stick', label: 'Press-and-Stick' },
  { value: 'waterproof', label: 'Waterproof' },
  { value: 'none', label: 'None' }
];

const MIDDLE_ICON_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'backpack', label: 'Backpack' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'house', label: 'House' },
  { value: 'iron', label: 'Iron' },
  { value: 'lunchbox', label: 'Lunchbox' },
  { value: 'present', label: 'Present' },
  { value: 'sippy-cup', label: 'Sippy Cup' },
  { value: 'shoe', label: 'Shoe' },
  { value: 'tag', label: 'Tagged' },
  { value: 'tagless', label: 'Tagless' },
  { value: 'tent', label: 'Tent' }
];

const ADDITIONAL_TEXT_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'laminated', label: 'Apply to clothing tag' },
  { value: 'unlaminated', label: 'Apply to clothing tag or size imprint' },
  { value: 'none', label: 'None' }
];

const FOOTER_OPTIONS = [
  { value: undefined, label: undefined },
  { value: 'iron-on', label: 'Do not Cut. Peel label off sheet...' },
  {
    value: 'peel-and-stick',
    label: 'Peel. Stick. Enjoy. Works best on smooth, dry surfaces.'
  },
  { value: 'clean-dry', label: 'Place labels onto a clean, dry surface...' },
  { value: 'size-imprint', label: 'Place labels onto size imprint...' },
  {
    value: 'clothing-care',
    label: 'Press and stick labels to a clothing care tag...'
  },
  {
    value: 'wrap-around-pacifier',
    label: 'Wrap Around Pacifier Labels: Loop the label...'
  },
  {
    value: 'directions-pacifier',
    label: 'Directions: Loop the label through...'
  }
];

const INPUT_SIZE = SizeEnum.medium;
const INPUT_WIDTH = 'w-[350px]';
const INPUT_CLASSES = clsx(INPUT_WIDTH, 'truncate');

interface EditTemplateSelectionsProps {
  templateSelections: Partial<ProductTemplateSelections>;
  templateSelectionsList: Partial<ProductTemplateSelections>[];
  setTemplateSelectionsList: (
    templateSelectionsList: Partial<ProductTemplateSelections>[]
  ) => void;
  material: Material;
  readOnly?: boolean;
}

export function EditTemplateSelections({
  templateSelections,
  templateSelectionsList,
  setTemplateSelectionsList,
  material,
  readOnly = false
}: EditTemplateSelectionsProps) {
  const handleSelectionChange = useCallback(
    (
      event: ChangeEvent<HTMLSelectElement>,
      key: keyof ProductTemplateSelections
    ) => {
      const { value } = event.target;
      const { material_id, template_type } = templateSelections;
      const updatedTemplateSelectionsList = templateSelectionsList.map(
        templateSelectionsItem => {
          const isMaterialMatch =
            templateSelectionsItem.material_id === material_id;

          const isTemplateTypeMatch =
            templateSelectionsItem.template_type === template_type;

          const isMatch = isMaterialMatch && isTemplateTypeMatch;

          return isMatch
            ? { ...templateSelectionsItem, [key]: value }
            : templateSelectionsItem;
        }
      );

      setTemplateSelectionsList(updatedTemplateSelectionsList);
    },
    [templateSelectionsList, setTemplateSelectionsList, templateSelections]
  );

  const handleLeftIconChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'left_icon_id');
    },
    [handleSelectionChange]
  );

  const handleLeftColorChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'left_print_key');
    },
    [handleSelectionChange]
  );

  const handleLeftTextChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'left_text_id');
    },
    [handleSelectionChange]
  );

  const handleMiddleIconChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'middle_icon_id');
    },
    [handleSelectionChange]
  );

  const handleMiddleColorChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'middle_print_key');
    },
    [handleSelectionChange]
  );

  const handleAdditionalTextChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'additional_text_id');
    },
    [handleSelectionChange]
  );

  const handleFooterChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleSelectionChange(event, 'footer_type_id');
    },
    [handleSelectionChange]
  );

  let title = material.system_name;

  switch (templateSelections.template_type) {
    case 'clothing':
      title = `${material.system_name} (For Clothing)`;
      break;
    case 'mixed':
      title = `${material.system_name} (Mixed)`;
      break;
  }

  const {
    left_icon_id,
    left_print_key,
    left_text_id,
    middle_icon_id,
    middle_print_key,
    additional_text_id,
    footer_type_id
  } = templateSelections;

  return (
    <div className="flex flex-col gap-2 pb-3">
      <h2>{title}</h2>
      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Left Icon:"
        required
        options={LEFT_ICON_OPTIONS}
        value={left_icon_id}
        onChange={handleLeftIconChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Left Color:"
        required
        options={COLOR_OPTIONS}
        value={left_print_key}
        onChange={handleLeftColorChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Left Text:"
        required
        options={LEFT_TEXT_OPTIONS}
        value={left_text_id}
        onChange={handleLeftTextChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Middle Icon:"
        required
        options={MIDDLE_ICON_OPTIONS}
        value={middle_icon_id}
        onChange={handleMiddleIconChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Middle Color:"
        required
        options={COLOR_OPTIONS}
        value={middle_print_key}
        onChange={handleMiddleColorChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Additional Text:"
        required
        options={ADDITIONAL_TEXT_OPTIONS}
        value={additional_text_id}
        onChange={handleAdditionalTextChange}
        disabled={readOnly}
      />

      <Select
        className={INPUT_CLASSES}
        size={INPUT_SIZE}
        contentBefore="Footer:"
        required
        options={FOOTER_OPTIONS}
        value={footer_type_id}
        onChange={handleFooterChange}
        disabled={readOnly}
      />
    </div>
  );
}
